import { User, CalendarIcon, Clock, Navigation } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useEffect, useRef, useState } from "react";

import FormInput from "@/app/_module/components/FormInput";
import { PhoneInput } from "@/app/_module/components/PhoneInput";
import { Button } from "@/app/_module/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/_module/components/ui/dropdown-menu";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/app/_module/components/ui/form";
import { Input } from "@/app/_module/components/ui/input";
import { Calendar } from "@/app/_module/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/_module/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";

import { genderOptions, type Gender } from "../constants/form-options";
import { type UpdateLeadSchema } from "../schema/lead-schema";

import { FormSection } from "./FormSection";

interface GeneralSectionProps {
  isEditing?: boolean; // Make optional since always editing now
  leadData?: any; // Add leadData to check if beneficiary
  bare?: boolean; // When true, render fields only without FormSection/Form wrapper
}

export const GeneralSection = function ({ isEditing = true, leadData, bare = false }: GeneralSectionProps) {
  const form = useFormContext<UpdateLeadSchema>();

  const {
    control,
    formState: { errors },
    watch,
    setValue,
    register,
  } = form;

  const gender = watch("gender");
  const dob = watch("dob");
  const percentage = watch("percentage");
  const address = watch("address");
  const currentState = watch("state");
  const watchedCity = watch("city" as any) as string | undefined;
  const watchedZip = watch("zipCode" as any) as string | undefined;

  const isAppleDevice = (): boolean => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const isIPadMac = /Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1;
    return /iPad|iPhone|iPod/.test(ua) || isIPadMac;
  };
  const buildNavDestination = (): string | null => {
    // Send the ADDRESS STRING to Google/Apple Maps so they use their own
    // (more accurate) geocoder. Coords are a fallback only — our geocoded
    // lat/lng may not match the actual house number perfectly.
    const parts = [address, watchedCity, currentState, watchedZip].filter(Boolean).join(", ");
    if (parts) return encodeURIComponent(parts);
    const lat = (leadData as any)?.latitude;
    const lng = (leadData as any)?.longitude;
    if (Number.isFinite(lat) && Number.isFinite(lng)) return `${lat},${lng}`;
    return null;
  };
  const openGoogleMapsForLead = () => {
    const dest = buildNavDestination();
    if (!dest) return;
    const webUrl = `https://www.google.com/maps/dir/?api=1&destination=${dest}`;
    if (isAppleDevice()) {
      const appUrl = `comgooglemaps://?daddr=${dest}&directionsmode=driving`;
      const start = Date.now();
      window.location.href = appUrl;
      setTimeout(() => {
        if (Date.now() - start < 2000 && document.visibilityState === "visible") {
          window.open(webUrl, "_blank");
        }
      }, 1200);
      return;
    }
    window.open(webUrl, "_blank");
  };
  const openAppleMapsForLead = () => {
    const dest = buildNavDestination();
    if (!dest) return;
    window.open(`https://maps.apple.com/?daddr=${dest}`, "_blank");
  };
  const hasAddress = !!(address || watchedCity || watchedZip);

  // State to timezone mapping for US states
  const stateTimezones: Record<string, { timezone: string; label: string }> = {
    // Eastern Time
    CT: { timezone: "America/New_York", label: "Eastern" },
    DE: { timezone: "America/New_York", label: "Eastern" },
    DC: { timezone: "America/New_York", label: "Eastern" },
    FL: { timezone: "America/New_York", label: "Eastern" }, // Most of FL
    GA: { timezone: "America/New_York", label: "Eastern" },
    IN: { timezone: "America/Indiana/Indianapolis", label: "Eastern" },
    KY: { timezone: "America/Kentucky/Louisville", label: "Eastern" }, // Most of KY
    ME: { timezone: "America/New_York", label: "Eastern" },
    MD: { timezone: "America/New_York", label: "Eastern" },
    MA: { timezone: "America/New_York", label: "Eastern" },
    MI: { timezone: "America/Detroit", label: "Eastern" },
    NH: { timezone: "America/New_York", label: "Eastern" },
    NJ: { timezone: "America/New_York", label: "Eastern" },
    NY: { timezone: "America/New_York", label: "Eastern" },
    NC: { timezone: "America/New_York", label: "Eastern" },
    OH: { timezone: "America/New_York", label: "Eastern" },
    PA: { timezone: "America/New_York", label: "Eastern" },
    RI: { timezone: "America/New_York", label: "Eastern" },
    SC: { timezone: "America/New_York", label: "Eastern" },
    VT: { timezone: "America/New_York", label: "Eastern" },
    VA: { timezone: "America/New_York", label: "Eastern" },
    WV: { timezone: "America/New_York", label: "Eastern" },
    // Central Time
    AL: { timezone: "America/Chicago", label: "Central" },
    AR: { timezone: "America/Chicago", label: "Central" },
    IL: { timezone: "America/Chicago", label: "Central" },
    IA: { timezone: "America/Chicago", label: "Central" },
    KS: { timezone: "America/Chicago", label: "Central" }, // Most of KS
    LA: { timezone: "America/Chicago", label: "Central" },
    MN: { timezone: "America/Chicago", label: "Central" },
    MS: { timezone: "America/Chicago", label: "Central" },
    MO: { timezone: "America/Chicago", label: "Central" },
    NE: { timezone: "America/Chicago", label: "Central" }, // Most of NE
    ND: { timezone: "America/Chicago", label: "Central" }, // Most of ND
    OK: { timezone: "America/Chicago", label: "Central" },
    SD: { timezone: "America/Chicago", label: "Central" }, // Most of SD
    TN: { timezone: "America/Chicago", label: "Central" }, // Most of TN
    TX: { timezone: "America/Chicago", label: "Central" }, // Most of TX
    WI: { timezone: "America/Chicago", label: "Central" },
    // Mountain Time
    AZ: { timezone: "America/Phoenix", label: "Mountain (no DST)" },
    CO: { timezone: "America/Denver", label: "Mountain" },
    ID: { timezone: "America/Boise", label: "Mountain" }, // Most of ID
    MT: { timezone: "America/Denver", label: "Mountain" },
    NV: { timezone: "America/Los_Angeles", label: "Pacific" }, // Most of NV is Pacific
    NM: { timezone: "America/Denver", label: "Mountain" },
    UT: { timezone: "America/Denver", label: "Mountain" },
    WY: { timezone: "America/Denver", label: "Mountain" },
    // Pacific Time
    CA: { timezone: "America/Los_Angeles", label: "Pacific" },
    OR: { timezone: "America/Los_Angeles", label: "Pacific" },
    WA: { timezone: "America/Los_Angeles", label: "Pacific" },
    // Alaska & Hawaii
    AK: { timezone: "America/Anchorage", label: "Alaska" },
    HI: { timezone: "Pacific/Honolulu", label: "Hawaii" },
  };

  // Get client's current time based on their state
  const [clientTime, setClientTime] = useState<string>("");

  useEffect(() => {
    const updateClientTime = () => {
      if (currentState && stateTimezones[currentState]) {
        const { timezone } = stateTimezones[currentState];
        const now = new Date();
        const timeStr = now.toLocaleTimeString("en-US", {
          timeZone: timezone,
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
        setClientTime(timeStr);
      } else {
        setClientTime("");
      }
    };

    updateClientTime();
    // Update every minute
    const interval = setInterval(updateClientTime, 60000);
    return () => clearInterval(interval);
  }, [currentState]);

  // Check if this lead is a beneficiary (has parentLeadId or isBeneficiary flag)
  const isBeneficiary = leadData?.parentLeadId || leadData?.isBeneficiary;

  // Track if user has manually interacted with state/zip fields
  // This prevents auto-extraction from overwriting user's intentional changes
  const userEditedStateRef = useRef(false);
  const userEditedZipRef = useRef(false);
  const hasInitializedRef = useRef(false);

  // Calculate age from date of birth
  // Uses UTC date extraction to avoid timezone shift issues with date-only strings
  const calculateAge = (birthDate: string): number => {
    if (!birthDate) return 0;
    const today = new Date();

    // Parse birth date - handle both YYYY-MM-DD and ISO formats
    let birthYear: number, birthMonth: number, birthDay: number;
    if (birthDate.includes('T')) {
      // ISO format - extract UTC components to avoid timezone shift
      const utcDate = new Date(birthDate);
      birthYear = utcDate.getUTCFullYear();
      birthMonth = utcDate.getUTCMonth();
      birthDay = utcDate.getUTCDate();
    } else {
      // YYYY-MM-DD format - parse directly to avoid timezone interpretation
      const [year, month, day] = birthDate.split('-').map(Number);
      birthYear = year;
      birthMonth = month - 1; // 0-indexed
      birthDay = day;
    }

    let age = today.getFullYear() - birthYear;
    const monthDiff = today.getMonth() - birthMonth;
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDay)) {
      age--;
    }
    return age;
  };

  // Age is calculated from DOB but not stored in the form
  // It can be calculated on-the-fly when needed
  const calculatedAge = dob ? calculateAge(dob) : null;

  // Date constraints for DOB - max 120 years ago, not future
  const today = new Date();
  const maxDobDate = today.toISOString().split('T')[0]; // Today
  const minDobDate = new Date(today.getFullYear() - 120, today.getMonth(), today.getDate())
    .toISOString().split('T')[0]; // 120 years ago

  // State abbreviation mapping for autofill
  const stateNameToAbbr: Record<string, string> = {
    'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
    'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
    'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID',
    'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS',
    'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
    'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS',
    'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV',
    'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
    'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK',
    'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
    'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT',
    'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV',
    'wisconsin': 'WI', 'wyoming': 'WY'
  };

  // Extract state from address string
  const extractStateFromAddress = (addressValue: string): string | null => {
    if (!addressValue) return null;

    // Pattern 1: State abbreviation before zip code (e.g., ", CA 90210" or ", CA, 90210" or "CA 90210")
    const stateAbbrMatch = addressValue.match(/[,\s]([A-Z]{2})[\s,]+\d{5}/);
    if (stateAbbrMatch) return stateAbbrMatch[1];

    // Pattern 2: State abbreviation at end without zip (e.g., ", CA" or " CA")
    const stateAbbrEndMatch = addressValue.match(/[,\s]([A-Z]{2})\s*$/);
    if (stateAbbrEndMatch) return stateAbbrEndMatch[1];

    // Pattern 3: State abbreviation anywhere with space boundaries (e.g., "Los Angeles CA" or "City, CA,")
    const stateAbbrAnyMatch = addressValue.match(/\b([A-Z]{2})\b/g);
    if (stateAbbrAnyMatch) {
      // Check if any match is a valid state
      const validStates = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
        'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN',
        'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK',
        'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
      for (const match of stateAbbrAnyMatch) {
        if (validStates.includes(match)) {
          return match;
        }
      }
    }

    // Pattern 4: Full state name in address
    const lowerAddress = addressValue.toLowerCase();
    for (const [stateName, abbr] of Object.entries(stateNameToAbbr)) {
      if (lowerAddress.includes(stateName)) {
        return abbr;
      }
    }
    return null;
  };

  // Extract zip code from address string
  const extractZipFromAddress = (addressValue: string): string | null => {
    if (!addressValue) return null;
    // Match 5-digit zip code
    const zipMatch = addressValue.match(/\b(\d{5})(?:-\d{4})?\b/);
    return zipMatch ? zipMatch[1] : null;
  };

  // Mark fields as initialized once we have initial values loaded
  useEffect(() => {
    if (!hasInitializedRef.current && (currentState || form.getValues('zipCode'))) {
      hasInitializedRef.current = true;
    }
  }, [currentState, form]);

  // Watch address changes to extract state and zip (React Hook Form watch)
  // Only auto-extract if user hasn't manually edited the fields
  useEffect(() => {
    if (!address) return;

    // Extract state if not already set and user hasn't edited it
    if (!currentState && !userEditedStateRef.current) {
      const extractedState = extractStateFromAddress(address);
      if (extractedState) {
        setValue("state", extractedState);
      }
    }

    // Also extract zip code if not already set and user hasn't edited it
    const currentZip = form.getValues('zipCode');
    if (!currentZip && !userEditedZipRef.current) {
      const extractedZip = extractZipFromAddress(address);
      if (extractedZip) {
        setValue("zipCode", extractedZip);
      }
    }
  }, [address, currentState, setValue, form]);

  // Also listen for browser autofill via DOM events, polling, and MutationObserver
  useEffect(() => {
    const addressInput = document.getElementById('address') as HTMLInputElement;
    if (!addressInput) return;

    let lastValue = addressInput.value;

    const checkForAutofill = () => {
      const currentValue = addressInput.value;
      const currentFormState = form.getValues('state');
      const currentFormZip = form.getValues('zipCode');

      if (currentValue && currentValue !== lastValue) {
        lastValue = currentValue;

        // Update the form value for address too (in case browser autofill didn't trigger React)
        const formAddress = form.getValues('address');
        if (formAddress !== currentValue) {
          setValue("address", currentValue, { shouldDirty: true });
        }

        // Extract and set state if not already set and user hasn't manually edited
        if (!currentFormState && !userEditedStateRef.current) {
          const extractedState = extractStateFromAddress(currentValue);
          if (extractedState) {
            setValue("state", extractedState);
          }
        }

        // Extract and set zip if not already set and user hasn't manually edited
        if (!currentFormZip && !userEditedZipRef.current) {
          const extractedZip = extractZipFromAddress(currentValue);
          if (extractedZip) {
            setValue("zipCode", extractedZip);
          }
        }
      }
    };

    // PERFORMANCE FIX: Check periodically for autofill (reduced from 200ms to 1000ms)
    const interval = setInterval(checkForAutofill, 1000);

    // MutationObserver to detect attribute changes (Chrome sets value via attribute)
    const observer = new MutationObserver(() => {
      setTimeout(checkForAutofill, 50);
    });
    observer.observe(addressInput, { attributes: true, attributeFilter: ['value'] });

    // Also listen for animation events (Chrome fires this on autofill)
    const handleAnimationStart = (e: AnimationEvent) => {
      if (e.animationName.includes('autofill')) {
        setTimeout(checkForAutofill, 100);
      }
    };

    // Focus/blur events can also trigger autofill detection
    const handleFocusBlur = () => {
      setTimeout(checkForAutofill, 100);
    };

    addressInput.addEventListener('animationstart', handleAnimationStart as EventListener);
    addressInput.addEventListener('change', checkForAutofill);
    addressInput.addEventListener('input', checkForAutofill);
    addressInput.addEventListener('blur', handleFocusBlur);

    // Stop polling after 30 seconds (extended time)
    const timeout = setTimeout(() => clearInterval(interval), 30000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
      observer.disconnect();
      addressInput.removeEventListener('animationstart', handleAnimationStart as EventListener);
      addressInput.removeEventListener('change', checkForAutofill);
      addressInput.removeEventListener('input', checkForAutofill);
      addressInput.removeEventListener('blur', handleFocusBlur);
    };
  }, [setValue, form]);

  // Monitor the hidden state autofill input
  useEffect(() => {
    const stateInput = document.getElementById('state-autofill') as HTMLInputElement;
    if (!stateInput) return;

    const validStates = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
      'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN',
      'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK',
      'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];

    let lastValue = '';

    const checkStateAutofill = () => {
      const currentValue = stateInput.value?.toUpperCase();
      if (currentValue && currentValue !== lastValue) {
        lastValue = currentValue;
        const currentFormState = form.getValues('state');
        if (!currentFormState) {
          if (validStates.includes(currentValue)) {
            setValue("state", currentValue);
          } else {
            // Try to match full state name
            const found = Object.entries(stateNameToAbbr).find(
              ([name]) => name === currentValue.toLowerCase()
            );
            if (found) {
              setValue("state", found[1]);
            }
          }
        }
      }
    };

    // PERFORMANCE FIX: Poll for changes (reduced from 200ms to 1000ms)
    const interval = setInterval(checkStateAutofill, 1000);

    // Listen for events
    stateInput.addEventListener('change', checkStateAutofill);
    stateInput.addEventListener('input', checkStateAutofill);

    const timeout = setTimeout(() => clearInterval(interval), 30000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
      stateInput.removeEventListener('change', checkStateAutofill);
      stateInput.removeEventListener('input', checkStateAutofill);
    };
  }, [setValue, form]);

  const fields = (
        <div className="space-y-4 w-full">

          <div className="w-full grid grid-cols-2 gap-4">
            <FormField
              control={control}
              name="name"
              render={({ field }) => (
                <FormInput
                  required
                  containerClass="space-y-2"
                  disabled={!isEditing}
                  error={errors.name}
                  id="name"
                  label="Full Name"
                  placeholder="Enter full name"
                  {...field}
                />
              )}
            />

            <FormField
              control={control}
              name="phone"
              render={({ field }) => (
                <PhoneInput
                  containerClass="space-y-2"
                  disabled={!isEditing}
                  error={errors.phone}
                  id="phone"
                  label="Phone Number"
                  placeholder="(555) 123-4567"
                  value={field.value || ""}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          <div className="w-full grid grid-cols-2 gap-4">
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormInput
                containerClass="space-y-2"
                disabled={!isEditing}
                error={errors.email}
                id="email"
                label="Email Address"
                placeholder="john@example.com"
                type="email"
                {...field}
              />
            )}
          />

          <FormField
            control={control}
            name="dob"
            render={({ field }) => (
              <FormItem className="space-y-0 flex flex-col gap-[2px]">
                <FormLabel className="text-sm !m-[2px]">Date of Birth</FormLabel>
                <Popover>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type="date"
                        value={field.value || ""}
                        onChange={(e) => {
                          let value = e.target.value;
                          // Limit year to 4 digits max and validate range
                          if (value) {
                            const parts = value.split('-');
                            if (parts[0]) {
                              if (parts[0].length > 4) {
                                parts[0] = parts[0].slice(0, 4);
                                value = parts.join('-');
                              }
                              const year = parseInt(parts[0], 10);
                              const currentYear = new Date().getFullYear();
                              if (year > currentYear) {
                                parts[0] = String(currentYear);
                                value = parts.join('-');
                              }
                            }
                          }
                          field.onChange(value);
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value) {
                            if (value > maxDobDate) {
                              field.onChange(maxDobDate);
                            } else if (value < minDobDate) {
                              field.onChange(minDobDate);
                            }
                          }
                          field.onBlur();
                        }}
                        disabled={!isEditing}
                        className="w-full pr-10"
                        min={minDobDate}
                        max={maxDobDate}
                      />
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                          disabled={!isEditing}
                        >
                          <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </PopoverTrigger>
                    </div>
                  </FormControl>
                  <PopoverContent align="start" className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={field.value ? new Date(field.value + 'T12:00:00') : undefined}
                      onSelect={(date) => {
                        if (date) {
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          field.onChange(`${year}-${month}-${day}`);
                        }
                      }}
                      disabled={(date) => {
                        const minDate = new Date(minDobDate + 'T00:00:00');
                        const maxDate = new Date(maxDobDate + 'T23:59:59');
                        return date > maxDate || date < minDate;
                      }}
                      defaultMonth={field.value ? new Date(field.value + 'T12:00:00') : new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
          </div>

          <div className="w-full grid grid-cols-2 gap-4">
          {/* Age is calculated from DOB, display only */}
          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700">Age</div>
            <input
              type="text"
              className="flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-80"
              value={calculatedAge !== null ? calculatedAge.toString() : ''}
              disabled
              placeholder="Calculated from DOB"
            />
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700">
              Gender
            </div>
            <Select
              disabled={!isEditing}
              onValueChange={(value: Gender) => setValue("gender", value)}
              value={gender || ""}
            >
              <SelectTrigger className={`${errors.gender ? "border-red-500" : ""} ${!isEditing ? "opacity-80 cursor-not-allowed bg-muted" : ""}`}>
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent>
                {genderOptions.map((gender) => (
                  <SelectItem key={gender} value={gender}>
                    {gender}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!!errors.gender && <p className="text-sm text-red-600">{errors.gender.message}</p>}
          </div>
          </div>

          <div className="w-full grid grid-cols-2 gap-4">
          <FormField
            control={control}
            name="height"
            render={({ field }) => (
              <FormInput
                containerClass="space-y-2"
                disabled={!isEditing}
                error={errors.height}
                id="height"
                label="Height"
                placeholder=""
                type="text"
                {...field}
                onChange={(e) => {
                  // Only allow numbers, apostrophe, and quote
                  const value = e.target.value.replace(/[^0-9'"]/g, '');
                  field.onChange(value);
                }}
                onBlur={(e) => {
                  // Format as feet'inches" on blur if it's just numbers
                  let value = e.target.value.replace(/[^0-9]/g, '');
                  if (value && !e.target.value.includes("'")) {
                    // Assume input like "510" means 5'10"
                    if (value.length === 3) {
                      // e.g., "510" -> "5'10""
                      const feet = value.slice(0, 1);
                      const inches = value.slice(1);
                      value = `${feet}'${inches}"`;
                    } else if (value.length === 2) {
                      // e.g., "60" -> "6'0""
                      const feet = value.slice(0, 1);
                      const inches = value.slice(1);
                      value = `${feet}'${inches}"`;
                    } else if (value.length === 1) {
                      // e.g., "6" -> "6'0""
                      value = `${value}'0"`;
                    } else if (value.length > 3) {
                      // e.g., "5106" -> "5'10"" (take first 3 digits)
                      const feet = value.slice(0, 1);
                      const inches = value.slice(1, 3);
                      value = `${feet}'${inches}"`;
                    }
                    field.onChange(value);
                  }
                  field.onBlur();
                }}
              />
            )}
          />

          <FormField
            control={control}
            name="weight"
            render={({ field }) => (
              <div className="space-y-2">
                <FormInput
                  containerClass="space-y-2"
                  disabled={!isEditing}
                  error={errors.weight}
                  id="weight"
                  label="Weight (lbs)"
                  placeholder=""
                  type="text"
                  {...field}
                  onChange={(e) => {
                    // Only allow numbers
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    field.onChange(value);
                  }}
                />
              </div>
            )}
          />
          </div>

          <div className="w-full relative">
            <FormField
              control={control}
              name="address"
              render={({ field }) => (
                <FormInput
                  containerClass="space-y-2"
                  disabled={!isEditing}
                  error={errors.address}
                  id="address"
                  label="Street Address"
                  placeholder="123 Main Street"
                  type="text"
                  autoComplete="street-address"
                  {...field}
                />
              )}
            />
            {hasAddress && (
              <div className="absolute right-0 top-0">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-primary hover:bg-primary/10 gap-1.5"
                      title="Navigate to this address"
                    >
                      <Navigation className="w-3.5 h-3.5 fill-current" />
                      <span className="text-xs font-medium">Navigate</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuItem onClick={openGoogleMapsForLead}>
                      <span className="text-sm">Google Maps</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={openAppleMapsForLead}>
                      <span className="text-sm">Apple Maps</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>

          <div className="w-full">
            <FormField
              control={control}
              name={"city" as any}
              render={({ field }) => (
                <FormInput
                  containerClass="space-y-2"
                  disabled={!isEditing}
                  id="city"
                  label="City"
                  placeholder=""
                  type="text"
                  autoComplete="address-level2"
                  {...field}
                  value={field.value || ""}
                />
              )}
            />
          </div>

          <div className="w-full grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700">
                State
              </div>
              {/* Hidden input for browser autofill - syncs with the Select */}
              <input
                type="text"
                id="state-autofill"
                name="state"
                autoComplete="address-level1"
                className="sr-only"
                tabIndex={-1}
                aria-hidden="true"
                onChange={(e) => {
                  const val = e.target.value?.toUpperCase();
                  // Check if it's a valid state abbreviation
                  const validStates = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
                    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN',
                    'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK',
                    'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
                  if (validStates.includes(val)) {
                    setValue("state", val);
                  } else {
                    // Try to find by full state name
                    const found = Object.entries(stateNameToAbbr).find(
                      ([name]) => name === val.toLowerCase()
                    );
                    if (found) {
                      setValue("state", found[1]);
                    }
                  }
                }}
              />
              <Select
                disabled={!isEditing}
                onValueChange={(value) => {
                  // Mark that user has manually edited this field
                  userEditedStateRef.current = true;
                  setValue("state", value);
                }}
                value={watch("state") || ""}
              >
                <SelectTrigger className={`${errors.state ? "border-red-500" : ""} ${!isEditing ? "opacity-80 cursor-not-allowed bg-muted" : ""}`}>
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AL">Alabama</SelectItem>
                  <SelectItem value="AK">Alaska</SelectItem>
                  <SelectItem value="AZ">Arizona</SelectItem>
                  <SelectItem value="AR">Arkansas</SelectItem>
                  <SelectItem value="CA">California</SelectItem>
                  <SelectItem value="CO">Colorado</SelectItem>
                  <SelectItem value="CT">Connecticut</SelectItem>
                  <SelectItem value="DE">Delaware</SelectItem>
                  <SelectItem value="DC">District of Columbia</SelectItem>
                  <SelectItem value="FL">Florida</SelectItem>
                  <SelectItem value="GA">Georgia</SelectItem>
                  <SelectItem value="HI">Hawaii</SelectItem>
                  <SelectItem value="ID">Idaho</SelectItem>
                  <SelectItem value="IL">Illinois</SelectItem>
                  <SelectItem value="IN">Indiana</SelectItem>
                  <SelectItem value="IA">Iowa</SelectItem>
                  <SelectItem value="KS">Kansas</SelectItem>
                  <SelectItem value="KY">Kentucky</SelectItem>
                  <SelectItem value="LA">Louisiana</SelectItem>
                  <SelectItem value="ME">Maine</SelectItem>
                  <SelectItem value="MD">Maryland</SelectItem>
                  <SelectItem value="MA">Massachusetts</SelectItem>
                  <SelectItem value="MI">Michigan</SelectItem>
                  <SelectItem value="MN">Minnesota</SelectItem>
                  <SelectItem value="MS">Mississippi</SelectItem>
                  <SelectItem value="MO">Missouri</SelectItem>
                  <SelectItem value="MT">Montana</SelectItem>
                  <SelectItem value="NE">Nebraska</SelectItem>
                  <SelectItem value="NV">Nevada</SelectItem>
                  <SelectItem value="NH">New Hampshire</SelectItem>
                  <SelectItem value="NJ">New Jersey</SelectItem>
                  <SelectItem value="NM">New Mexico</SelectItem>
                  <SelectItem value="NY">New York</SelectItem>
                  <SelectItem value="NC">North Carolina</SelectItem>
                  <SelectItem value="ND">North Dakota</SelectItem>
                  <SelectItem value="OH">Ohio</SelectItem>
                  <SelectItem value="OK">Oklahoma</SelectItem>
                  <SelectItem value="OR">Oregon</SelectItem>
                  <SelectItem value="PA">Pennsylvania</SelectItem>
                  <SelectItem value="RI">Rhode Island</SelectItem>
                  <SelectItem value="SC">South Carolina</SelectItem>
                  <SelectItem value="SD">South Dakota</SelectItem>
                  <SelectItem value="TN">Tennessee</SelectItem>
                  <SelectItem value="TX">Texas</SelectItem>
                  <SelectItem value="UT">Utah</SelectItem>
                  <SelectItem value="VT">Vermont</SelectItem>
                  <SelectItem value="VA">Virginia</SelectItem>
                  <SelectItem value="WA">Washington</SelectItem>
                  <SelectItem value="WV">West Virginia</SelectItem>
                  <SelectItem value="WI">Wisconsin</SelectItem>
                  <SelectItem value="WY">Wyoming</SelectItem>
                </SelectContent>
              </Select>
              {!!errors.state && <p className="text-sm text-red-600">{errors.state.message}</p>}
            </div>

            <FormField
              control={control}
              name="zipCode"
              render={({ field }) => (
                <FormInput
                  containerClass="space-y-2"
                  disabled={!isEditing}
                  error={errors.zipCode}
                  id="zipCode"
                  label="Zip Code"
                  placeholder=""
                  type="text"
                  autoComplete="postal-code"
                  {...field}
                  onChange={(e) => {
                    // Mark that user has manually edited this field
                    userEditedZipRef.current = true;
                    // Only allow numbers and limit to 5 digits
                    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 5);
                    field.onChange(value);
                  }}
                />
              )}
            />
          </div>

          {/* Client's Local Time based on State */}
          {currentState && stateTimezones[currentState] && clientTime && (
            <div className="w-full">
              <div className="flex items-center gap-2 p-3 bg-primary/10 border border-primary/20 rounded-lg">
                <Clock className="h-4 w-4 text-primary" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    Local Time: <span className="text-gray-900">{clientTime}</span>
                  </p>
                  <p className="text-xs text-gray-600">
                    {stateTimezones[currentState].label} Time ({currentState})
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Show Percentage field only for beneficiaries */}
          {isBeneficiary && (
            <div className="w-full">
              <FormField
                control={control}
                name="percentage"
                render={({ field }) => (
                  <div className="space-y-2">
                    <label htmlFor="percentage" className="text-sm font-medium text-gray-700">
                      Beneficiary Percentage
                    </label>
                    <div className="relative">
                      <input
                        {...field}
                        id="percentage"
                        type="text"
                        placeholder="50"
                        disabled={!isEditing}
                        className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-80 disabled:bg-muted pr-8 ${errors.percentage ? "border-red-500" : ""} ${!isEditing ? "opacity-80 cursor-not-allowed bg-muted" : ""}`}
                        onChange={(e) => {
                          // Only allow numbers and decimal point
                          let value = e.target.value.replace(/[^0-9.]/g, '');
                          // Ensure only one decimal point
                          const parts = value.split('.');
                          value = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          // Limit to 100
                          const numValue = parseFloat(value);
                          if (!isNaN(numValue) && numValue > 100) {
                            value = '100';
                          }
                          field.onChange(value);
                        }}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                        %
                      </span>
                    </div>
                    {!!errors.percentage && <p className="text-sm text-red-600">{errors.percentage.message}</p>}
                  </div>
                )}
              />
            </div>
          )}
        </div>
  );

  if (bare) return fields;

  return (
    <FormSection className="w-full" icon={User} title="General Information">
      <Form {...form}>
        {fields}
      </Form>
    </FormSection>
  );
};
